// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import assertNever from "assert-never";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTypedParams } from "react-router-typesafe-routes/dom";

import { checkInSections } from "~@jii/configs";
import { State } from "~@jii/paths";

import { useCheckInAnalytics } from "./hooks/useCheckInAnalytics";
import { useCheckInData } from "./hooks/useCheckInData";
import { useCheckInFlow } from "./hooks/useCheckInFlow";
import { UsNeCheckInCompletion } from "./UsNeCheckInCompletion/UsNeCheckInCompletion";
import { UsNeCheckInContextProvider } from "./UsNeCheckInContext";
import { UsNeCheckInExitModal } from "./UsNeCheckInExitModal/UsNeCheckInExitModal";
import { UsNeCheckInLayout } from "./UsNeCheckInLayout/UsNeCheckInLayout";
import { UsNeCheckInQuestion } from "./UsNeCheckInQuestion/UsNeCheckInQuestion";
import { UsNeCheckInSectionIntro } from "./UsNeCheckInSectionIntro/UsNeCheckInSectionIntro";
import { UsNeCheckInWelcome } from "./UsNeCheckInWelcome/UsNeCheckInWelcome";

export const UsNeCheckInPage = () => {
  return (
    <UsNeCheckInContextProvider>
      <UsNeCheckInPageContent />
    </UsNeCheckInContextProvider>
  );
};

const UsNeCheckInPageContent = () => {
  const { data, updateMutation } = useCheckInData();
  const saveIsPending = updateMutation.isPending;
  const screen = useCheckInFlow(data);
  const sectionId =
    screen.screenKind === "question" || screen.screenKind === "sectionIntro"
      ? screen.sectionId
      : undefined;
  const questionId =
    screen.screenKind === "question" ? screen.questionId : undefined;

  const trackEvent = useCheckInAnalytics(data.id);
  useEffect(() => {
    trackEvent("frontend_ne_check_in_page_viewed", {
      screenKind: screen.screenKind,
      sectionId,
      questionId,
    });
  }, [trackEvent, screen.screenKind, sectionId, questionId]);

  const showModal = screen.screenKind === "question";
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const routeParams = useTypedParams(State.Resident);
  const navigate = useNavigate();
  const exit = () => {
    if (saveIsPending) return;
    trackEvent("frontend_ne_check_in_exited", {});
    navigate(State.Resident.buildPath(routeParams));
  };
  const openModal = () => {
    if (saveIsPending) return;
    trackEvent("frontend_ne_check_in_exit_modal_opened", {});
    setModalIsOpen(true);
  };
  const onCloseClick = showModal ? openModal : exit;

  const renderContent = () => {
    switch (screen.screenKind) {
      case "completion":
        return <UsNeCheckInCompletion onClose={exit} />;
      case "welcome":
      case "instructions":
        return <UsNeCheckInWelcome kind={screen.screenKind} />;
      case "sectionIntro":
        return <UsNeCheckInSectionIntro sectionId={screen.sectionId} />;
      case "question":
        return (
          <UsNeCheckInQuestion
            questionId={screen.questionId}
            sectionId={screen.sectionId}
            data={data}
            updateMutation={updateMutation}
          />
        );
      default:
        return assertNever(screen);
    }
  };

  return (
    <UsNeCheckInLayout
      onCloseClick={onCloseClick}
      sectionId={sectionId}
      allSections={checkInSections[data.version]}
    >
      {renderContent()}
      {showModal && (
        <UsNeCheckInExitModal
          isOpen={modalIsOpen}
          onStay={() => {
            trackEvent("frontend_ne_check_in_exit_modal_closed", {});
            setModalIsOpen(false);
          }}
          onLeave={exit}
        />
      )}
    </UsNeCheckInLayout>
  );
};
